import React, { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './components/Home';
import MoreAboutMe from './components/MoreAboutMe';
import Resume from './components/Resume';
import SuccessModal from './components/SuccessModal';

function App() {
  const [showModal, setShowModal] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Get API base URL from environment variable or use defaults
  const getApiBase = () => {
    // Check for custom API URL (for Railway backend)
    if (process.env.REACT_APP_API_URL) {
      return process.env.REACT_APP_API_URL;
    }
    // Development: use localhost
    if (process.env.NODE_ENV === 'development') {
      return 'http://localhost:5000';
    }
    // Production: use relative path (same domain) - won't work with Railway, need REACT_APP_API_URL
    return '';
  };

  // Check maintenance mode status on app load
  useEffect(() => {
    const checkMaintenanceMode = async () => {
      try {
        const apiBase = getApiBase();
        const apiUrl = apiBase ? `${apiBase}/api` : '/api';
        
        const response = await fetch(`${apiUrl}/settings/maintenance-mode`);
        
        // Check if response is OK and is JSON
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('Response is not JSON');
        }
        
        const data = await response.json();
        setMaintenanceMode(data.enabled);
      } catch (error) {
        // Silently fail - default to false if API is unavailable
        // This is expected when backend is not deployed yet
        console.warn('Maintenance mode check skipped - backend not available:', error.message);
        setMaintenanceMode(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkMaintenanceMode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFormSubmit = async (formData) => {
    const apiBase = getApiBase();
    
    // If no API URL is configured, show helpful message
    if (!apiBase && process.env.NODE_ENV === 'production') {
      alert('Backend is not configured. Please set REACT_APP_API_URL environment variable in Vercel settings to point to your Railway backend.');
      console.error('REACT_APP_API_URL is not set. Backend connection required for form submission.');
      return;
    }
    
    try {
      const apiUrl = apiBase ? `${apiBase}/api/service-requests` : '/api/service-requests';
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setShowModal(true);
      } else {
        const contentType = response.headers.get('content-type');
        const isJson = contentType && contentType.includes('application/json');
        const errorText = isJson 
          ? (await response.json()).error || 'Failed to submit request'
          : `Server error: ${response.status} ${response.statusText}`;
        
        console.error('Failed to submit form:', errorText);
        alert(`Failed to submit request: ${errorText}. Please try again or contact support.`);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      
      // Provide helpful error message
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        alert('Unable to connect to the server. Please check your internet connection or contact support if the problem persists.');
      } else {
        alert('Failed to submit request. Please try again or contact support.');
      }
    }
  };

  const closeModal = () => {
    setShowModal(false);
  };

  // Show maintenance page if maintenance mode is enabled
  if (maintenanceMode) {
    return (
      <div className="App maintenance-page">
        <div className="maintenance-container">
          <div className="maintenance-content">
            <div className="maintenance-icon">
              <i className="fas fa-tools"></i>
            </div>
            <h1>Site Under Maintenance</h1>
            <p>We're currently performing scheduled maintenance to improve your experience.</p>
            <p>We'll be back online shortly. Thank you for your patience!</p>
            <div className="maintenance-contact">
              <p>For urgent inquiries, please contact:</p>
              <a href="mailto:contact@awardwebservices.net">contact@awardwebservices.net</a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading spinner while checking maintenance mode
  if (isLoading) {
    return (
      <div className="App loading-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Show normal site content
  return (
    <div className="App">
      <Header />
      <Routes>
        <Route path="/" element={<Home onFormSubmit={handleFormSubmit} />} />
        <Route path="/more-about-me" element={<MoreAboutMe />} />
        <Route path="/resume" element={<Resume />} />
      </Routes>
      <Footer />
      <SuccessModal show={showModal} onClose={closeModal} />
    </div>
  );
}

export default App; 